import React, { useEffect } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
export function RefundPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const goBack = () => {
    window.location.hash = '';
  };
  return <div className="min-h-screen bg-white text-black" data-id="element-598">
      <div className="max-w-3xl mx-auto px-4 py-12 md:py-20" data-id="element-599">
        <button onClick={goBack} className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary transition-colors mb-8" data-id="element-600">
          <ArrowLeftIcon size={16} data-id="element-601" />
          Back to Home
        </button>

        <h1 className="text-3xl md:text-4xl font-black text-black mb-2" data-id="element-602">
          Refunds &amp; Cancellation Policy
        </h1>
        <p className="text-sm text-gray-400 mb-10" data-id="element-603">
          Last updated: 16 March 2026
        </p>

        <div className="prose prose-gray max-w-none space-y-8 text-sm md:text-base leading-relaxed text-gray-700" data-id="element-604">
          <section data-id="element-605">
            <h2 className="text-lg md:text-xl font-bold text-black mb-3" data-id="element-606">
              1. No Refund Policy
            </h2>
            <p data-id="element-607">
              All payments made to <strong data-id="element-608">Strattigize</strong> ("Company",
              "we", "us", or "our") for subscription services are{' '}
              <strong data-id="element-609">strictly non-refundable</strong>. By subscribing to any of
              our plans (Starter, Unlimited, or Pro), you acknowledge and agree
              that:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-610">
              <li data-id="element-611">
                No refunds will be issued under any circumstances, including but
                not limited to dissatisfaction with the Services, change of
                mind, business closure, or failure to utilise the Services
                during the subscription period.
              </li>
              <li data-id="element-612">
                Partial refunds for unused portions of a billing cycle will not
                be provided.
              </li>
              <li data-id="element-613">
                This no-refund policy applies to all subscription plans and any
                add-on services purchased.
              </li>
            </ul>
            <p data-id="element-614">
              This policy is in accordance with the Consumer Protection Act,
              2019, which permits service providers to establish and enforce
              their own refund policies provided they are clearly communicated
              to the consumer prior to the transaction. By completing your
              subscription, you confirm that you have read, understood, and
              accepted this no-refund policy.
            </p>
          </section>

          <section data-id="element-615">
            <h2 className="text-lg md:text-xl font-bold text-black mb-3" data-id="element-616">
              2. Rationale for No Refund Policy
            </h2>
            <p data-id="element-617">
              Our Services involve significant upfront investment of time,
              resources, and expertise, including but not limited to:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-618">
              <li data-id="element-619">
                Custom website design, development, and deployment within 24
                hours of onboarding.
              </li>
              <li data-id="element-620">
                Configuration and setup of CRM systems, automation workflows,
                and AI tools tailored to your business.
              </li>
              <li data-id="element-621">
                Allocation of dedicated server resources, third-party software
                licences, and AI processing capacity.
              </li>
              <li data-id="element-622">
                Ongoing maintenance, monitoring, and support infrastructure.
              </li>
            </ul>
            <p data-id="element-623">
              Due to the immediate nature of service delivery and the
              irrecoverable costs incurred upon activation, refunds are not
              feasible.
            </p>
          </section>

          <section data-id="element-624">
            <h2 className="text-lg md:text-xl font-bold text-black mb-3" data-id="element-625">
              3. Cancellation Policy
            </h2>
            <p data-id="element-626">
              You may cancel your subscription at any time by following the
              procedure outlined below. Cancellation does not entitle you to a
              refund of any payments already made.
            </p>

            <h3 className="text-base md:text-lg font-bold text-black mt-4 mb-2" data-id="element-627">
              3.1 How to Cancel
            </h3>
            <p data-id="element-628">
              To cancel your subscription, you must send a written cancellation
              request via email to{' '}
              <a href="mailto:strattigize@gmail.com" className="text-primary font-medium hover:underline" data-id="element-629">
                strattigize@gmail.com
              </a>{' '}
              at least <strong data-id="element-630">7 (seven) days</strong> before your next billing
              date. The cancellation request must include:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-631">
              <li data-id="element-632">Your full name and registered email address.</li>
              <li data-id="element-633">
                The subscription plan you wish to cancel (Starter, Unlimited, or
                Pro).
              </li>
              <li data-id="element-634">The reason for cancellation (optional but appreciated).</li>
            </ul>

            <h3 className="text-base md:text-lg font-bold text-black mt-4 mb-2" data-id="element-635">
              3.2 Effect of Cancellation
            </h3>
            <p data-id="element-636">Upon successful cancellation:</p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-637">
              <li data-id="element-638">
                Your subscription will remain active until the end of the
                current billing cycle. You will continue to have access to all
                Services until that date.
              </li>
              <li data-id="element-639">
                No further charges will be applied to your payment method after
                the current billing period ends.
              </li>
              <li data-id="element-640">
                All Services, including your website, automations, AI chatbot,
                AI receptionist, CRM access, and review management systems, will
                be <strong data-id="element-641">deactivated</strong> at the end of the billing
                period.
              </li>
              <li data-id="element-642">
                Any data associated with your account may be retained for up to
                30 days after deactivation, after which it may be permanently
                deleted without further notice.
              </li>
            </ul>

            <h3 className="text-base md:text-lg font-bold text-black mt-4 mb-2" data-id="element-643">
              3.3 Late Cancellation
            </h3>
            <p data-id="element-644">
              If a cancellation request is received less than 7 days before the
              next billing date, the cancellation will take effect at the end of
              the <strong data-id="element-645">following</strong> billing cycle, and you will be
              charged for one additional month.
            </p>
          </section>

          <section data-id="element-646">
            <h2 className="text-lg md:text-xl font-bold text-black mb-3" data-id="element-647">
              4. Chargebacks &amp; Disputed Payments
            </h2>
            <p data-id="element-648">
              Filing a chargeback or payment dispute with your bank or payment
              provider without first contacting us to resolve the issue is
              considered a breach of these terms. In the event of an
              unauthorised chargeback:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-649">
              <li data-id="element-650">
                All Services will be immediately suspended pending resolution.
              </li>
              <li data-id="element-651">
                The Company reserves the right to pursue recovery of the
                disputed amount along with any associated administrative costs
                through appropriate legal channels under Indian law.
              </li>
              <li data-id="element-652">
                The Company may report the incident to relevant credit agencies
                and fraud prevention services.
              </li>
            </ul>
          </section>

          <section data-id="element-653">
            <h2 className="text-lg md:text-xl font-bold text-black mb-3" data-id="element-654">
              5. Service Modifications
            </h2>
            <p data-id="element-655">
              The Company reserves the right to modify, update, or discontinue
              any feature or aspect of the Services at any time. Such
              modifications do not constitute grounds for a refund. We will make
              reasonable efforts to notify subscribers of significant changes in
              advance.
            </p>
          </section>

          <section data-id="element-656">
            <h2 className="text-lg md:text-xl font-bold text-black mb-3" data-id="element-657">
              6. Exceptions
            </h2>
            <p data-id="element-658">
              In rare and exceptional circumstances, the Company may, at its
              sole and absolute discretion, consider providing a credit towards
              future services (not a monetary refund) if:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-659">
              <li data-id="element-660">
                The Company has materially failed to deliver the core Services
                as described in the subscription plan for a continuous period
                exceeding 15 days, and the failure is solely attributable to the
                Company.
              </li>
              <li data-id="element-661">
                The Client has provided written notice of the issue and allowed
                the Company a reasonable period (minimum 7 days) to rectify the
                matter.
              </li>
            </ul>
            <p data-id="element-662">
              Any such credit is entirely at the Company's discretion and does
              not create a precedent or obligation for future cases.
            </p>
          </section>

          <section data-id="element-663">
            <h2 className="text-lg md:text-xl font-bold text-black mb-3" data-id="element-664">
              7. Governing Law
            </h2>
            <p data-id="element-665">
              This Refunds &amp; Cancellation Policy is governed by and
              construed in accordance with the laws of the Republic of India,
              including the Consumer Protection Act, 2019, the Indian Contract
              Act, 1872, and the Information Technology Act, 2000. Any disputes
              arising from this policy shall be subject to the exclusive
              jurisdiction of the courts in India.
            </p>
          </section>

          <section data-id="element-666">
            <h2 className="text-lg md:text-xl font-bold text-black mb-3" data-id="element-667">
              8. Contact Us
            </h2>
            <p data-id="element-668">
              If you have any questions about this policy or wish to initiate a
              cancellation, please contact us at:
            </p>
            <p className="font-medium text-black" data-id="element-669">
              Strattigize
              <br data-id="element-670" />
              Email:{' '}
              <a href="mailto:strattigize@gmail.com" className="text-primary hover:underline" data-id="element-671">
                strattigize@gmail.com
              </a>
            </p>
          </section>

          <section className="bg-gray-50 border border-gray-200 rounded-xl p-5 md:p-6" data-id="element-672">
            <p className="text-xs md:text-sm text-gray-500 leading-relaxed" data-id="element-673">
              <strong className="text-gray-700" data-id="element-674">Important Notice:</strong> By
              completing your subscription payment, you expressly acknowledge
              that you have read, understood, and agreed to this No Refund and
              Cancellation Policy. This acknowledgement constitutes your
              informed consent as required under the Consumer Protection Act,
              2019.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-675">
          <button onClick={goBack} className="flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors" data-id="element-676">
            <ArrowLeftIcon size={16} data-id="element-677" />
            Back to Home
          </button>
        </div>
      </div>
    </div>;
}